import { inject, Injectable, OnDestroy } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, map, Subject, throwError } from 'rxjs';
import { toIdentifiableEntities } from 'src/app/helpers/graphql';
import { CommunicationProviderType } from 'src/app/models/communication-provider';
import { NotificationSchedule } from 'src/app/models/notifications';
import { ToastService } from 'src/app/services/toast/toast.service';
import { TranslationService } from 'src/app/services/translation/translation.service';
import {
  addListEntity,
  ListEntities,
  ListEntitiesToIterable,
  loadEntities,
  NewListEntities,
  removeListEntity,
  updateListEntity,
} from '../common/list-entity';
import {
  CreateWebPushRecipientEndpointAction,
  LoadRecipientEndpointsForOrganizationMemberAction,
} from '../communication/communication.actions';
import { CommunicationState } from '../communication/communication.state';
import { UserState } from '../user/user.state';
import {
  AskForPushAction,
  CreateNotificationScheduleAction,
  DeleteNotificationScheduleAction,
  EnablePushNotificationsAction,
  LoadNotificationSchedulesAction,
  SendTestNotificationForScheduleAction,
  SetIgnoreAskForPushUntilAction,
  UpdateNotificationScheduleAction,
} from './notifications.actions';
import { NotificationsService } from './notifications.service';
import { ConfirmService } from 'src/app/services/dialog/confirm.service';

const CHECK_NOTIFICATIONS_INTERVAL = 1000 * 60 * 60; // 1 hour
const ASK_FOR_PUSH_INTERVAL = 1000 * 60 * 60 * 24 * 7; // 1 week
// const CHECK_NOTIFICATIONS_INTERVAL = 1000; // 1 hour
// const ASK_FOR_PUSH_INTERVAL = 1000 * 10;

export interface NotificationsStateModel {
  lastChecked?: Date;
  ignoreAskForPushUntil?: Date;
  hasPushConfigured?: boolean;
  schedules?: ListEntities<NotificationSchedule>;
}

@State<NotificationsStateModel>({
  name: 'notifications',
  children: [],
  defaults: {
    schedules: NewListEntities<NotificationSchedule>(),
  },
})
@Injectable()
export class NotificationsState implements OnDestroy {
  private store = inject(Store);
  private confirmService = inject(ConfirmService);
  private notificationsService = inject(NotificationsService);
  private toastService = inject(ToastService);
  private translationService = inject(TranslationService);

  private destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @Selector()
  public static schedules(state: NotificationsStateModel) {
    return state.schedules;
  }

  @Action(AskForPushAction)
  askForPushNotifications(ctx: StateContext<NotificationsStateModel>) {
    const lastChecked = ctx.getState().lastChecked;

    if (
      !lastChecked ||
      lastChecked.getTime() <
        new Date().getTime() - CHECK_NOTIFICATIONS_INTERVAL
    ) {
      ctx
        .dispatch(new LoadRecipientEndpointsForOrganizationMemberAction())
        .subscribe(() => {
          const endpoints = this.store.selectSnapshot(
            CommunicationState.orgMemberRecipientEndpoints
          );

          const hasPushConfigured = ListEntitiesToIterable(
            endpoints
          ).entities.some(
            e => e.data?.providerType === CommunicationProviderType.FCM
          );

          ctx.patchState({
            lastChecked: new Date(),
            hasPushConfigured: hasPushConfigured,
          });

          // If push notifications are not configured, ask the user to enable them
          if (!hasPushConfigured) {
            this.promptUserForPushNotifications(ctx);
          }
        });
    }
  }

  private promptUserForPushNotifications(
    ctx: StateContext<NotificationsStateModel>
  ) {
    const ignoreAskForPushUntil = ctx.getState().ignoreAskForPushUntil;

    if (
      ignoreAskForPushUntil &&
      ignoreAskForPushUntil.getTime() > new Date().getTime()
    ) {
      return;
    }

    const ns = 'components.pushNotificationsDialog';
    this.confirmService
      .confirm({
        title: `${ns}.header`,
        description: `${ns}.description`,
        confirmLabel: `${ns}.confirm`,
        cancelLabel: `${ns}.cancel`,
        tone: 'primary',
        icon: 'bell',
      })
      .subscribe(ok => {
        if (ok) {
          ctx.dispatch(new EnablePushNotificationsAction());
        } else {
          const ignoreUntil = new Date(
            new Date().getTime() + ASK_FOR_PUSH_INTERVAL
          );
          ctx.dispatch(new SetIgnoreAskForPushUntilAction(ignoreUntil));
        }
      });
  }

  @Action(EnablePushNotificationsAction)
  enablePushNotifications(ctx: StateContext<NotificationsStateModel>) {
    return ctx.dispatch(new CreateWebPushRecipientEndpointAction());
  }

  @Action(SetIgnoreAskForPushUntilAction)
  setIgnoreAskForPushUntil(
    ctx: StateContext<NotificationsStateModel>,
    action: SetIgnoreAskForPushUntilAction
  ) {
    ctx.patchState({
      ignoreAskForPushUntil: action.ignoreUntil,
    });
  }

  @Action(LoadNotificationSchedulesAction)
  loadNotificationSchedules(
    ctx: StateContext<NotificationsStateModel>,
    action: LoadNotificationSchedulesAction
  ) {
    const { organizationId, memberId, token } =
      action.getOrgAndMemberIdFromTokenWithFallback(() => {
        const membership = this.store.selectSnapshot(UserState.membership);
        if (!membership) {
          throw new Error('No membership set');
        }
        return [membership.organizationId, membership.id];
      });

    return loadEntities(
      ctx,
      'schedules',
      this.notificationsService
        .listNotificationSchedules(organizationId, memberId, token)
        .pipe(
          map(schedules => {
            return toIdentifiableEntities(schedules, 'id');
          })
        )
    );
  }

  @Action(DeleteNotificationScheduleAction)
  deleteNotificationSchedule(
    ctx: StateContext<NotificationsStateModel>,
    action: DeleteNotificationScheduleAction
  ) {
    const { organizationId, token } =
      action.getOrgAndMemberIdFromTokenWithFallback(() => {
        const membership = this.store.selectSnapshot(UserState.membership);
        if (!membership) {
          throw new Error('No membership set');
        }
        return [membership.organizationId, membership.id];
      });

    return removeListEntity(
      ctx,
      'schedules',
      action.scheduleId,
      (schedule: NotificationSchedule) => {
        return schedule.id !== action.scheduleId;
      },
      this.notificationsService
        .deleteNotificationSchedule(organizationId, action.scheduleId, token)
        .pipe(
          map(val => {
            if (val) {
              this.toastService.showInfo(
                this.translationService.translateInstantKey(
                  'resources.notificationSchedule.toasts.deleted.header'
                ),
                this.translationService.translateInstantKey(
                  'resources.notificationSchedule.toasts.deleted.description'
                )
              );
            }

            return val;
          })
        )
    );
  }

  @Action(UpdateNotificationScheduleAction)
  updateNotificationSchedule(
    ctx: StateContext<NotificationsStateModel>,
    action: UpdateNotificationScheduleAction
  ) {
    const { organizationId, token } =
      action.getOrgAndMemberIdFromTokenWithFallback(() => {
        const membership = this.store.selectSnapshot(UserState.membership);
        if (!membership) {
          throw new Error('No membership set');
        }
        return [membership.organizationId, membership.id];
      });

    return updateListEntity(
      ctx,
      'schedules',
      action.scheduleId,
      this.notificationsService
        .updateNotificationSchedule(
          organizationId,
          action.scheduleId,
          action.spec,
          token
        )
        .pipe(
          map(val => {
            if (val) {
              this.toastService.showInfo(
                this.translationService.translateInstantKey(
                  'resources.notificationSchedule.toasts.updated.header'
                ),
                this.translationService.translateInstantKey(
                  'resources.notificationSchedule.toasts.updated.description'
                )
              );
            }

            return val;
          }),
          catchError(err => {
            this.toastService.showError(
              new Error('Error updating schedule: ' + err.message)
            );
            return throwError(() => err);
          })
        )
    );
  }

  @Action(CreateNotificationScheduleAction)
  createNotificationSchedule(
    ctx: StateContext<NotificationsStateModel>,
    action: CreateNotificationScheduleAction
  ) {
    const membership = this.store.selectSnapshot(UserState.membership);

    if (!membership) {
      throw new Error('No membership set');
    }

    return addListEntity(
      ctx,
      'schedules',
      'id',
      this.notificationsService
        .createNotificationSchedule(
          membership.organizationId,
          membership.id,
          action.spec
        )
        .pipe(
          map(val => {
            if (val) {
              this.toastService.showInfo(
                this.translationService.translateInstantKey(
                  'resources.notificationSchedule.toasts.created.header'
                ),
                this.translationService.translateInstantKey(
                  'resources.notificationSchedule.toasts.created.description'
                )
              );
            }

            return val;
          }),
          catchError(err => {
            this.toastService.showError(
              new Error('Error creating schedule: ' + err.message)
            );
            return throwError(() => err);
          })
        )
    );
  }

  @Action(SendTestNotificationForScheduleAction)
  sendTestNotificationForSchedule(
    ctx: StateContext<NotificationsStateModel>,
    action: SendTestNotificationForScheduleAction
  ) {
    const membership = this.store.selectSnapshot(UserState.membership);

    if (!membership) {
      throw new Error('No membership set');
    }

    return this.notificationsService
      .sendTestNotificationForSchedule(
        membership.organizationId,
        action.scheduleId
      )
      .pipe(
        map(val => {
          if (val) {
            this.toastService.showInfo(
              this.translationService.translateInstantKey(
                'resources.notificationSchedule.toasts.testNotificationSent.header'
              ),
              this.translationService.translateInstantKey(
                'resources.notificationSchedule.toasts.testNotificationSent.description'
              )
            );
          }

          return val;
        }),
        catchError(err => {
          this.toastService.showError(
            new Error('Error sending test notification: ' + err.message)
          );
          return throwError(() => err);
        })
      );
  }
}
